/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';

import { Grid } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import OnboardingSearch from 'bundles/onboarding-common/components/OnboardingSearch';
import { INPUT_TYPES } from 'bundles/onboarding-common/constants';
import type {
  AutoCompleteableInputProps,
  Occupation,
  Option,
} from 'bundles/onboarding-common/types/AutoCompleteableInputProps';

import _t from 'i18n!nls/onboarding-common';

const styles: Record<string, Interpolation<Theme>> = {
  hidden: css`
    display: none;
  `,
  dropdown: css`
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-blue-700);
    overflow-y: auto;
    max-height: 240px;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    z-index: 1000;
    padding: var(--cds-spacing-200) 0;
  `,
  option: css`
    z-index: 2;
    text-align: left;
    background-color: var(--cds-color-neutral-background-primary);
    border: none;
    padding: var(--cds-spacing-150);
    margin: 0 var(--cds-spacing-200);

    &:focus {
      background-color: var(--cds-color-blue-50);
      color: var(--cds-color-blue-800);
      outline: none;
    }

    &:hover {
      background-color: var(--cds-color-blue-50);
      color: var(--cds-color-blue-800);
    }
  `,
  createOption: css`
    z-index: 2;
    text-align: left;
    background-color: var(--cds-color-blue-50);
    color: var(--cds-color-blue-800);
    border: none;
    padding: var(--cds-spacing-150);
    margin: 0 var(--cds-spacing-200);
  `,
  searchInput: css`
    // Remove the focus outline from the input
    .cds-input-focused {
      &::before {
        display: none;
      }
    }
  `,
};

export const AutoCompleteableInput: React.FC<AutoCompleteableInputProps> = (
  { allOptions, defaultValue, questionId, onChange, question, disabled = false },
  context
) => {
  const [options, setOptions] = useState<Option[] | []>([]);

  const [query, setQuery] = useState('');

  const [value, setValue] = useState(defaultValue);

  const [exactMatch, setExactMatch] = useState({ id: '', name: '' });

  const [highlightCreateOption, setHighlightCreateOption] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const isClearingRef = useRef(false);

  // Create an empty array and fill it with refs when generating buttons for options.
  // We always filter a maximum of 10 options so the array of option references can always be of length 11 (to account for the "create option" button as well)
  const optionRefArr = useMemo(() => new Array(11), []);

  const setOptionRef = (ref: HTMLButtonElement | HTMLInputElement | null, index: number) => {
    optionRefArr[index] = ref;
  };

  const [optionIndex, setOptionIndex] = useState(0);

  const saveValue = (newValue: Occupation, inputType = INPUT_TYPES.DEFAULT) => {
    setValue(newValue);
    Retracked.trackComponent(
      context._eventData,
      { questionId, query: query.trim(), value: { id: newValue.id, name: newValue.name, inputType } },
      'onboarding_input',
      'saveValue'
    );
    if (inputType === INPUT_TYPES.DEFAULT) {
      onChange({
        [questionId]: { id: newValue.id },
      });
    } else {
      onChange({
        [questionId]: { otherText: newValue.id },
      });
    }
  };

  const handleOnClick = (id: string, name: string, inputType = INPUT_TYPES.DEFAULT) => {
    saveValue({ id, name }, inputType);

    setQuery('');
  };

  useEffect(
    () => {
      // Handlers for Basic Interatctions
      const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
          setQuery('');
        }
      };
      const handleTabOutside = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
          if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
            setQuery('');
          }
        }
      };
      const handleEscapeInput = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setQuery('');
        }
      };

      // Handle backspace or delete on empty input to reset to default
      const handleDeleteOption = (event: KeyboardEvent) => {
        if (event.key === 'Delete' || event.key === 'Backspace') {
          if (inputRef.current && inputRef.current.contains(event.target as Node)) {
            if (query.length <= 1 && value.name.length > 0) {
              Retracked.trackComponent(
                context._eventData,
                {
                  questionId,
                  query: query.trim(),
                  value: { id: value.id, name: value.name, inputType: value.__typename, exactMatch },
                },
                'onboarding_input',
                'removeInput'
              );
              saveValue({ id: '', name: '' });
            }
          }
        }
      };
      const handleEnterInput = (event: KeyboardEvent) => {
        if (inputRef.current && inputRef.current.contains(event.target as Node)) {
          if (event.key === 'Enter') {
            if (exactMatch.name) {
              saveValue(exactMatch, 'QuestionId');
            } else {
              saveValue({ id: query.trim(), name: query.trim() }, INPUT_TYPES.USER_GENERATED);
            }
            setQuery('');
          }
        }
      };
      const handleArrowKeyNav = (event: KeyboardEvent) => {
        if (query.length > 1) {
          if (event.key === 'ArrowDown') {
            if (optionRefArr[optionIndex] && optionRefArr[optionIndex + 1]) {
              optionRefArr[optionIndex + 1].focus();
              setOptionIndex(optionIndex + 1);
              event.preventDefault();
            } else if (optionRefArr[optionIndex]) {
              optionRefArr[0].focus();
              setOptionIndex(0);
              event.preventDefault();
            }
          }
          if (event.key === 'ArrowUp') {
            if (optionRefArr[optionIndex] && optionRefArr[optionIndex - 1]) {
              optionRefArr[optionIndex - 1].focus();
              setOptionIndex(optionIndex - 1);
              event.preventDefault();
            }
            if (optionIndex === 0 && options) {
              optionRefArr[options.length].focus();
              setOptionIndex(options.length);
              event.preventDefault();
            }
          }
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keyup', handleTabOutside);
      document.addEventListener('keyup', handleEscapeInput);
      document.addEventListener('keydown', handleDeleteOption);
      document.addEventListener('keydown', handleArrowKeyNav);
      document.addEventListener('keydown', handleEnterInput);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keyup', handleTabOutside);
        document.removeEventListener('keyup', handleEscapeInput);
        document.removeEventListener('keydown', handleDeleteOption);
        document.removeEventListener('keydown', handleArrowKeyNav);
        document.removeEventListener('keydown', handleEnterInput);
      };
    }, // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.length, optionIndex, query]
  );

  const checkExactMatch = (queryString: string, filteredOptions?: Occupation[]) => {
    const testableOptions = filteredOptions || options;

    if (testableOptions) {
      let foundMatch = false;
      for (let i = 0; i < testableOptions.length; i += 1) {
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        if (testableOptions[i].name.toLowerCase() === queryString) {
          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          setExactMatch({ id: testableOptions[i].id, name: testableOptions[i].name });
          foundMatch = true;
          break;
        }
      }
      if (!foundMatch) {
        setExactMatch({ id: '', name: '' });
      }
    }
  };

  const betterSearch = (phrase: string, userQuery?: string) => {
    const cleanPhrase = phrase.toLowerCase();
    const cleanUserQuery = userQuery?.toLowerCase() || '';
    const userQueryTokens = cleanUserQuery.split(' ');

    const partialMatchInFullString = cleanPhrase.includes(cleanUserQuery);
    const allTokensIncluded = userQueryTokens.every((word) => cleanPhrase.includes(word));

    return partialMatchInFullString || allTokensIncluded;
  };

  // getFilteredResult from bundles/account-profile/components/common/OccupationsDropdown.tsx
  const getFilteredResult = (queryString?: string) => {
    const isMinimumInput = queryString && queryString.length > 1;
    const lowercaseQuery = queryString?.toLowerCase() || '';

    const filteredOptions: Option[] =
      (isMinimumInput &&
        allOptions
          ?.filter((option: Option) => option.name && betterSearch(option.name, queryString))
          // sort by length
          .sort((optionA: Option, optionB: Option) => (optionA?.name?.length || 0) - (optionB?.name?.length || 0))
          // then only take 15 for performance reasons
          .slice(0, 15)) ||
      [];

    checkExactMatch(lowercaseQuery, filteredOptions);

    return filteredOptions;
  };

  const handleInputOnChange = (queryString: string) => {
    setQuery(queryString);

    // If user has cleared the input completely, reset the saved value
    if (queryString === '' && value.name.length > 0) {
      setValue({ id: '', name: '' });
      onChange({
        [questionId]: { id: '' },
      });
    }

    // using the query filter the options
    setOptions(getFilteredResult(queryString.trim()));
  };

  const handleInputOnFocus = () => {
    // If the focus is triggered by clearing the input, don't do anything
    if (isClearingRef.current) {
      isClearingRef.current = false;
      return;
    }
    setQuery(value.name || '');
    setOptionIndex(0);
    setOptions(getFilteredResult(value.name));
    setHighlightCreateOption(true);
  };

  const optionsWithoutExactMatch = exactMatch.name
    ? options?.filter((option: Option) => option.name.toLowerCase() !== exactMatch.name.toLowerCase())
    : options;

  return (
    <>
      <Grid
        container
        ref={wrapperRef}
        direction="column"
        css={css`
          position: relative;
        `}
        aria-live="polite"
      >
        <Grid item>
          <OnboardingSearch
            dataTestId="autocompletable-input"
            css={styles.searchInput}
            id={questionId}
            placeholder={value.name ? _t('Enter job title') : _t('Type to search')}
            value={query || value.name}
            onChange={(searchValue) => handleInputOnChange(searchValue)}
            onFocus={() => handleInputOnFocus()}
            onBlur={() => setHighlightCreateOption(false)}
            onClear={() => {
              isClearingRef.current = true;
              setQuery('');
              setValue({ id: '', name: '' });
              setOptions([]);
            }}
            autoComplete="off"
            ref={inputRef}
            disabled={disabled}
            ariaLabel={question}
          />
        </Grid>

        <Grid
          item
          container
          direction="column"
          wrap="nowrap"
          css={query.length > 0 ? styles.dropdown : styles.hidden}
          role="status"
        >
          {query.trim().length > 0 && exactMatch.name && (
            <button
              data-testid="autocompletable-input--option-button-exact"
              type="button"
              onClick={() => handleOnClick(exactMatch.id, exactMatch.name)}
              css={highlightCreateOption ? styles.createOption : styles.option}
              ref={(e) => setOptionRef(e, 0)}
              onFocus={() => setOptionIndex(0)}
              tabIndex={-1}
            >
              {exactMatch.name}
            </button>
          )}
          {query.trim().length > 0 && !exactMatch.name && (
            <button
              data-testid="autocompletable-input--option-button-create"
              type="button"
              onClick={() => handleOnClick(query.trim(), query.trim(), INPUT_TYPES.USER_GENERATED)}
              css={highlightCreateOption ? styles.createOption : styles.option}
              ref={(e) => setOptionRef(e, 0)}
              onFocus={() => setOptionIndex(0)}
              tabIndex={-1}
            >
              {_t('Create option')} &quot;{query.trim()}&quot;
            </button>
          )}
          {optionsWithoutExactMatch.map((option: Option, index: number) => (
            <button
              key={option.name}
              data-testid="autocompletable-input--option-item"
              type="button"
              onClick={() => handleOnClick(option.id, option.name)}
              css={styles.option}
              ref={(e) => setOptionRef(e, index + 1)}
              onFocus={() => setOptionIndex(index + 1)}
              tabIndex={-1}
              aria-label={`Suggestion number ${index + 1} : ` + option.name}
            >
              {option.name}
            </button>
          ))}
        </Grid>
      </Grid>
    </>
  );
};

AutoCompleteableInput.contextTypes = { _eventData: PropTypes.object };

export default AutoCompleteableInput;
